import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, PlayCircle, Ban, MessageCircle, KeyRound, ArrowLeft, Percent } from "lucide-react";

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fotoUrl = (idPublico) => `${API}/indicador-foto/${idPublico}`;
const waLink = (tel) => {
  const d = (tel || "").replace(/\D/g, "");
  if (d.length < 10) return null;
  return `https://wa.me/${d.startsWith("55") ? d : `55${d}`}`;
};

const STATUS_STYLE = {
  aguardando_aprovacao: "bg-amber-100 text-amber-700",
  ativo: "bg-emerald-100 text-emerald-700",
  rejeitado: "bg-red-100 text-red-600",
  suspenso: "bg-slate-200 text-slate-600",
  bloqueado: "bg-red-600 text-white",
};
const COM_STYLE = {
  aguardando_conclusao: "bg-slate-100 text-slate-600",
  liberada: "bg-blue-100 text-blue-700",
  paga: "bg-emerald-100 text-emerald-700",
  cancelada: "bg-red-100 text-red-600",
};

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-[11px] text-slate-400 font-semibold">{label}</span>
      <span className="text-[11px] text-navy font-bold text-right break-all" data-testid={testId}>{value ?? "—"}</span>
    </div>
  );
}

function PerfilInner({ user }) {
  const { indicadorId } = useParams();
  const [ind, setInd] = useState(null);
  const [carteira, setCarteira] = useState(null);
  const [motivoRej, setMotivoRej] = useState("");
  const [motivoCor, setMotivoCor] = useState("");
  const [showRej, setShowRej] = useState(false);
  const [showCor, setShowCor] = useState(false);
  const [bonusPct, setBonusPct] = useState("");
  const [bonusData, setBonusData] = useState("");
  const [bonusJust, setBonusJust] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/indicadores/${indicadorId}`).then((r) => setInd(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar"));
    api.get(`/admin/indicadores/${indicadorId}/carteira`).then((r) => setCarteira(r.data)).catch(() => {});
  }, [indicadorId]);

  useEffect(() => { load(); }, [load]);

  const acao = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na operação");
    }
  };

  if (!ind) return <AdminShell user={user} title="Indicador"><p className="px-4 text-xs text-slate-400">Carregando...</p></AdminShell>;

  const pix = ind.pix || {};

  return (
    <AdminShell user={user} title={`Indicador ${ind.id_publico}`}>
      <div className="px-4 space-y-4" data-testid="admin-indicador-perfil">
        <Link to="/admin/indicadores" className="inline-flex items-center gap-1 text-xs text-navy font-bold" data-testid="perfil-voltar"><ArrowLeft className="w-3.5 h-3.5" /> Voltar à gestão</Link>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-3 mb-3">
            {ind.foto ? (
              <img src={fotoUrl(ind.id_publico)} alt="Foto de perfil" className="w-16 h-16 rounded-2xl object-cover border-2 border-[#F5B301]" data-testid="perfil-foto" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-navy text-[#F5B301] flex items-center justify-center text-2xl font-extrabold">{ind.nome?.[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-navy" data-testid="perfil-nome">{ind.nome}</p>
              <p className="text-xs text-slate-500 font-bold" data-testid="perfil-id">{ind.id_publico}</p>
              <span className={`inline-block mt-1 text-[9px] font-extrabold px-2 py-0.5 rounded-full ${STATUS_STYLE[ind.status]}`} data-testid="perfil-status">{ind.status_label}</span>
            </div>
          </div>
          <Row label="CPF" value={ind.cpf} testId="perfil-cpf" />
          <Row label="Telefone/WhatsApp" value={ind.telefone} testId="perfil-telefone" />
          <Row label="E-mail" value={ind.email} testId="perfil-email" />
          <Row label="Cadastro em" value={fmtDt(ind.created_at)} />
          {ind.aprovado_em && <Row label="Aprovado em" value={`${fmtDt(ind.aprovado_em)} · por ${ind.aprovado_por}`} testId="perfil-aprovado" />}
          {ind.rejeitado_motivo && <Row label="Motivo da rejeição" value={ind.rejeitado_motivo} />}

          <div className="flex gap-1.5 mt-3 flex-wrap">
            {ind.status === "aguardando_aprovacao" && (
              <>
                <button onClick={() => window.confirm(`Aprovar ${ind.nome} (${ind.id_publico})?`) && acao(() => api.post(`/admin/indicadores/${ind.indicador_id}/aprovar`), "Cadastro aprovado — login, link e carteira liberados!")} data-testid="perfil-aprovar-btn" className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <CheckCircle2 className="w-3 h-3" /> APROVAR CADASTRO
                </button>
                <button onClick={() => setShowRej((v) => !v)} data-testid="perfil-rejeitar-btn" className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <XCircle className="w-3 h-3" /> REJEITAR
                </button>
              </>
            )}
            {ind.status === "ativo" && (
              <button onClick={() => window.confirm(`Suspender o acesso de ${ind.nome}?`) && acao(() => api.post(`/admin/indicadores/${ind.indicador_id}/status`, { acao: "suspender" }), "Indicador suspenso.")} data-testid="perfil-suspender-btn" className="px-3 py-2 rounded-lg bg-slate-200 text-slate-700 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                <PauseCircle className="w-3 h-3" /> SUSPENDER
              </button>
            )}
            {ind.status === "suspenso" && (
              <button onClick={() => acao(() => api.post(`/admin/indicadores/${ind.indicador_id}/status`, { acao: "reativar" }), "Indicador reativado.")} data-testid="perfil-reativar-btn" className="px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                <PlayCircle className="w-3 h-3" /> REATIVAR
              </button>
            )}
            {ind.status !== "bloqueado" && ind.status !== "aguardando_aprovacao" && (
              <button onClick={() => window.confirm(`BLOQUEAR definitivamente ${ind.nome}?`) && acao(() => api.post(`/admin/indicadores/${ind.indicador_id}/status`, { acao: "bloquear" }), "Indicador bloqueado.")} data-testid="perfil-bloquear-btn" className="px-3 py-2 rounded-lg bg-red-600 text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                <Ban className="w-3 h-3" /> BLOQUEAR
              </button>
            )}
            {waLink(ind.telefone) && (
              <a href={waLink(ind.telefone)} target="_blank" rel="noopener noreferrer" data-testid="perfil-whatsapp-btn" className="px-3 py-2 rounded-lg bg-[#25D366] text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                <MessageCircle className="w-3 h-3" /> CONTACTAR PELO WHATSAPP
              </a>
            )}
          </div>
          {showRej && (
            <div className="mt-2 flex gap-2" data-testid="perfil-rejeitar-form">
              <input className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs" placeholder="Motivo da rejeição (opcional)" value={motivoRej} onChange={(e) => setMotivoRej(e.target.value)} data-testid="perfil-rejeitar-motivo" />
              <button onClick={() => acao(() => api.post(`/admin/indicadores/${ind.indicador_id}/rejeitar`, { motivo: motivoRej }), "Cadastro rejeitado.")} data-testid="perfil-rejeitar-confirmar" className="px-3 py-2 rounded-lg bg-red-600 text-white text-[10px] font-extrabold">CONFIRMAR</button>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="perfil-pix-section">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Dados Pix para pagamento</h2>
          <span className={`inline-block text-[9px] font-extrabold px-2 py-1 rounded-full mb-2 ${pix.status === "aprovados" ? "bg-emerald-100 text-emerald-700" : pix.status === "necessita_correcao" ? "bg-red-100 text-red-600" : pix.status === "pendente_conferencia" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid="perfil-pix-status">
            {pix.status_label || "NÃO CADASTRADOS"}
          </span>
          {pix.chave ? (
            <>
              <Row label="Tipo" value={pix.tipo} testId="perfil-pix-tipo" />
              <Row label="Chave" value={pix.chave} testId="perfil-pix-chave" />
              <Row label="Titular" value={pix.titular_nome} testId="perfil-pix-titular" />
              <Row label="CPF/CNPJ titular" value={pix.titular_doc || "—"} />
              <div className="flex gap-1.5 mt-2">
                <button onClick={() => acao(() => api.put(`/admin/indicadores/${ind.indicador_id}/pix-status`, { status: "aprovados" }), "Dados Pix aprovados para pagamento.")} data-testid="pix-aprovar-btn" className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-extrabold active:scale-95 transition-transform">
                  APROVAR DADOS
                </button>
                <button onClick={() => setShowCor((v) => !v)} data-testid="pix-correcao-btn" className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-extrabold active:scale-95 transition-transform">
                  NECESSITA CORREÇÃO
                </button>
              </div>
              {showCor && (
                <div className="mt-2 flex gap-2" data-testid="pix-correcao-form">
                  <input className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs" placeholder="O que precisa corrigir?" value={motivoCor} onChange={(e) => setMotivoCor(e.target.value)} data-testid="pix-correcao-motivo" />
                  <button onClick={() => acao(() => api.put(`/admin/indicadores/${ind.indicador_id}/pix-status`, { status: "necessita_correcao", motivo: motivoCor }), "Correção solicitada ao indicador.")} data-testid="pix-correcao-confirmar" className="px-3 py-2 rounded-lg bg-red-600 text-white text-[10px] font-extrabold">ENVIAR</button>
                </div>
              )}
            </>
          ) : (
            <p className="text-[11px] text-slate-400">O indicador ainda não cadastrou os dados Pix.</p>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="perfil-bonus-section">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" /> Comissão individual (bônus)</h2>
          {ind.comissao_individual ? (
            <div className="rounded-xl bg-gold-light/50 border border-[#F5B301]/50 p-3 mb-2" data-testid="bonus-vigente">
              <p className="text-xs font-bold text-navy" data-testid="bonus-vigente-pct">Bônus vigente: {ind.comissao_individual.pct}%</p>
              <p className="text-[10px] text-slate-500">A partir de {ind.comissao_individual.data_inicio} · definido por {ind.comissao_individual.definido_por}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Vale para frete e mudança, em novos contratos confirmados a partir da data de início. Permanece até alteração manual — não altera os demais indicadores.</p>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 mb-2" data-testid="bonus-ausente">Sem bônus individual — vale o percentual global do Clube.</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="bonus-pct-input" inputMode="decimal" className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="% individual (ex: 6)" value={bonusPct} onChange={(e) => setBonusPct(e.target.value)} />
            <input data-testid="bonus-data-input" type="date" className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm" value={bonusData} onChange={(e) => setBonusData(e.target.value)} />
          </div>
          <input data-testid="bonus-just-input" className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder="Justificativa (ex: 1º lugar no ranking do mês)" value={bonusJust} onChange={(e) => setBonusJust(e.target.value)} />
          <div className="flex gap-2 mt-2">
            <button onClick={() => acao(() => api.put(`/admin/indicadores/${ind.indicador_id}/comissao-individual`, { pct: parseFloat(String(bonusPct).replace(",", ".")), data_inicio: bonusData, justificativa: bonusJust }), "Bônus individual salvo!")} data-testid="bonus-salvar-btn" className="flex-1 py-2.5 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform">
              SALVAR BÔNUS
            </button>
            {ind.comissao_individual && (
              <button onClick={() => window.confirm(`Remover o bônus individual de ${ind.nome}? Ele volta ao percentual global do Clube.`) && acao(() => api.put(`/admin/indicadores/${ind.indicador_id}/comissao-individual`, { pct: null, justificativa: bonusJust }), "Bônus removido — voltou ao percentual global.")} data-testid="bonus-remover-btn" className="px-3 py-2.5 rounded-xl bg-red-100 text-red-600 text-[10px] font-extrabold active:scale-95 transition-transform">
                REMOVER
              </button>
            )}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="perfil-carteira">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Carteira (visão administrativa)</h2>
          <div className="grid grid-cols-3 gap-1.5 mb-3 text-center">
            {[
              { l: "Indicados", v: ind.total_indicados ?? 0 },
              { l: "Contratados", v: ind.total_contratados ?? 0 },
              { l: "Pendentes", v: brl(ind.comissoes_pendentes) },
              { l: "Liberadas", v: brl(ind.comissoes_liberadas) },
              { l: "Pagas", v: brl(ind.comissoes_pagas) },
            ].map((a) => (
              <div key={a.l} className="bg-slate-50 rounded-lg py-2">
                <p className="text-xs font-extrabold text-navy">{a.v}</p>
                <p className="text-[8px] text-slate-400 uppercase font-semibold">{a.l}</p>
              </div>
            ))}
          </div>
          {(carteira?.comissoes || []).length === 0 && <p className="text-[11px] text-slate-400">Nenhuma comissão até o momento.</p>}
          {(carteira?.comissoes || []).map((c) => (
            <div key={c.comissao_id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 mb-2" data-testid={`perfil-comissao-${c.comissao_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-navy">{c.tipo === "frete" ? "FRETE" : "MUDANÇA"} · {brl(c.valor)}</p>
                <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full ${COM_STYLE[c.status]}`}>{c.status_label}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Base {brl(c.base_calculo)} · {c.percentual}% · criada {fmtDt(c.created_at)}</p>
              {(c.historico || []).slice(-3).map((h, i) => (
                <p key={i} className="text-[9px] text-slate-400 mt-0.5">• {h.acao} · {fmtDt(h.em)}{h.justificativa ? ` — ${h.justificativa}` : ""}</p>
              ))}
            </div>
          ))}
          <Link to="/admin/indicadores?f=financeiro" className="inline-block mt-1 text-[11px] font-bold text-navy underline" data-testid="perfil-ir-financeiro">
            Abrir financeiro para registrar pagamento
          </Link>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="perfil-historico">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Histórico administrativo</h2>
          {(ind.historico || []).length === 0 && <p className="text-[11px] text-slate-400">Sem registros.</p>}
          {[...(ind.historico || [])].reverse().map((h, i) => (
            <p key={i} className="text-[10px] text-slate-500 py-1 border-b border-slate-50 last:border-0">
              {fmtDt(h.em)} · {h.acao}{h.admin ? ` · por ${h.admin}` : ""}{h.motivo ? ` — ${h.motivo}` : ""}
            </p>
          ))}
        </section>
      </div>
    </AdminShell>
  );
}

export default function AdminIndicadorPerfil() {
  return <AdminGate>{(user) => <PerfilInner user={user} />}</AdminGate>;
}
